import React, { useEffect, useMemo, useRef, useState } from "react";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { Box, CircularProgress, Link, Popover, Skeleton, Stack, Typography } from "@mui/material";
import { Button, NumberInput } from "components/forms";
import { AISource, ObjectiveConstraint, ObjectiveRow } from "store/types";

import { usePortfolioConstructionContext } from "../Context/PortfolioConstructionContext";

const COLOR_PRIMARY = "#071f35";
const COLOR_SECONDARY = "#496682";
const COLOR_DISABLED = "#92a7bc";
const COLOR_STROKE_IDLE = "#7b92a9";
const COLOR_STROKE_DIVIDER = "#dbe8f5";
const COLOR_BG_SUBTLE = "#f6f8fc";
const COLOR_ERROR = "#d93e3e";
const COLOR_INFO = "#1c6db5";
const COLOR_AI = "#7c3aed";

export const OBJECTIVE_CONSTRAINTS: ObjectiveConstraint[] = [
  { key: "target-return", label: "Target return", suffix: "%" },
  { key: "max-vol", label: "Max volatility", suffix: "%" },
  { key: "max-drawdown", label: "Max drawdown", suffix: "%" },
  { key: "liquidity-floor", label: "Liquidity floor", suffix: "%" }
];

const isSameAsDefault = (row: ObjectiveRow) => row.source === "system-default";

const constraintByKey = (key: string) => OBJECTIVE_CONSTRAINTS.find((c) => c.key === key);

interface Props {
  variant?: "default" | "compact";
}

export const ObjectivesPanel: React.FC<Props> = ({ variant = "default" }) => {
  const {
    aum,
    updateAum,
    aumError,
    clearAumError,
    objectives,
    isLoadingObjectives,
    objectiveErrors,
    clearObjectiveError,
    updateObjective,
    applyAIObjective,
    resetObjective,
    resetAllObjectives,
    loadAISources,
    permissions
  } = usePortfolioConstructionContext();

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [aiAnchor, setAiAnchor] = useState<HTMLElement | null>(null);
  const [aiOpenKey, setAiOpenKey] = useState<string | null>(null);
  const [objectivesPanelCollapsed, setObjectivesPanelCollapsed] = useState(false);

  const handleHeaderToggle = () => setObjectivesPanelCollapsed(!objectivesPanelCollapsed);

  const handleResetAll = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await resetAllObjectives();
  };

  const startEdit = (key: string) => {
    if (!permissions.canEditObjectives) return;
    setEditingKey(key);
    clearObjectiveError(key);
  };

  const commitEdit = async (key: string, raw: string) => {
    const cleaned = raw.trim();
    if (cleaned === "") {
      setEditingKey(null);
      return;
    }
    const parsed = parseFloat(cleaned);
    if (!Number.isFinite(parsed)) {
      setEditingKey(null);
      return;
    }
    setEditingKey(null);
    // Skip the save if the value is unchanged (user opened the editor but didn't edit).
    const current = objectives?.find((o) => o.key === key)?.value;
    if (current !== undefined && parsed === current) return;
    await updateObjective(key, parsed);
  };

  const cancelEdit = () => {
    setEditingKey(null);
  };

  const handleAIToggle = (key: string, anchor: HTMLElement) => {
    if (aiOpenKey === key) {
      setAiOpenKey(null);
      setAiAnchor(null);
      return;
    }
    setAiAnchor(anchor);
    setAiOpenKey(key);
  };

  const closeAI = () => {
    setAiOpenKey(null);
    setAiAnchor(null);
  };

  const handleAIApply = async (key: string, source: AISource) => {
    // A source with no recommended value can't be applied (e.g. provider listed
    // but no figure published yet).
    if (source.value == null) return;
    closeAI();
    await applyAIObjective(key, source.label, source.value, source.url);
  };

  const handleRowReset = async (key: string) => {
    await resetObjective(key);
  };

  const headerSx = variant === "compact" ? { px: "12px", py: "10px" } : { px: "16px", py: "12px" };

  return (
    <Stack
      sx={{
        width: "100%",
        flexShrink: 0,
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0px 1px 2px 0px rgba(18, 18, 23, 0.1)",
        overflow: "hidden"
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={handleHeaderToggle}
        sx={{
          ...headerSx,
          cursor: "pointer",
          gap: "12px",
          userSelect: "none"
        }}
      >
        <Stack direction="row" alignItems="center" gap="8px" sx={{ minWidth: 0 }}>
          {objectivesPanelCollapsed ? (
            <ExpandMoreIcon sx={{ fontSize: 20, color: COLOR_PRIMARY }} />
          ) : (
            <ExpandLessIcon sx={{ fontSize: 20, color: COLOR_PRIMARY }} />
          )}
          <Stack sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 500,
                lineHeight: "20px",
                color: COLOR_PRIMARY
              }}
            >
              Objectives
            </Typography>
            <Typography
              sx={{
                fontSize: "12px",
                lineHeight: "16px",
                color: COLOR_SECONDARY
              }}
            >
              What this portfolio must achieve
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" alignItems="center" gap="8px">
          {permissions.canEditObjectives && (
            <Box
              component="button"
              onClick={handleResetAll}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                padding: "4px 10px",
                borderRadius: "20px",
                backgroundColor: "transparent",
                border: "none",
                cursor: "pointer",
                color: COLOR_PRIMARY,

                fontSize: "12px",
                fontWeight: 500,
                "&:hover": { backgroundColor: "rgba(7,31,53,0.06)" }
              }}
            >
              <RestartAltIcon sx={{ fontSize: 14 }} /> Reset all
            </Box>
          )}
        </Stack>
      </Stack>

      {!objectivesPanelCollapsed && (
        <Stack sx={{ borderTop: `1px solid ${COLOR_STROKE_DIVIDER}` }}>
          {isLoadingObjectives || !objectives ? (
            <Stack>
              <SkeletonRow />
              {OBJECTIVE_CONSTRAINTS.map((c) => (
                <SkeletonRow key={c.key} />
              ))}
            </Stack>
          ) : (
            <Stack>
              <AumRowView
                value={aum ?? 0}
                error={aumError ?? undefined}
                canEdit={permissions.canEditObjectives}
                onCommit={updateAum}
                onEditStart={clearAumError}
              />
              {OBJECTIVE_CONSTRAINTS.map((constraint) => {
                const row = objectives.find((o) => o.key === constraint.key);
                if (!row) return null;
                const isEditing = editingKey === constraint.key;
                const error = objectiveErrors[constraint.key];
                const canShowReset = permissions.canEditObjectives && !isSameAsDefault(row);
                return (
                  <ObjectiveRowView
                    key={constraint.key}
                    constraint={constraint}
                    row={row}
                    isEditing={isEditing}
                    error={error}
                    canEdit={permissions.canEditObjectives}
                    canShowReset={canShowReset}
                    aiOpen={aiOpenKey === constraint.key}
                    onStartEdit={() => startEdit(constraint.key)}
                    onCommitEdit={(v) => commitEdit(constraint.key, v)}
                    onCancelEdit={cancelEdit}
                    onToggleAI={(anchor) => handleAIToggle(constraint.key, anchor)}
                    onReset={() => handleRowReset(constraint.key)}
                  />
                );
              })}
            </Stack>
          )}
        </Stack>
      )}

      <Popover
        open={Boolean(aiOpenKey && aiAnchor)}
        anchorEl={aiAnchor}
        onClose={closeAI}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: "4px",
            width: 360,
            maxWidth: "calc(100vw - 32px)",
            borderRadius: "8px",
            boxShadow:
              "0px 2px 4px -1px rgba(0, 0, 0, 0.20), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)"
          }
        }}
      >
        {aiOpenKey && (
          <AIPopoverContent
            constraintKey={aiOpenKey}
            loadAISources={loadAISources}
            onApply={handleAIApply}
            onClose={closeAI}
          />
        )}
      </Popover>
    </Stack>
  );
};

const SkeletonRow: React.FC = () => (
  <Stack
    direction="row"
    alignItems="center"
    gap="12px"
    sx={{
      padding: "10px 16px",
      borderBottom: `1px solid ${COLOR_STROKE_DIVIDER}`,
      "&:last-of-type": { borderBottom: "none" }
    }}
  >
    <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
      <Skeleton variant="text" width="60%" height={20} />
    </Box>
    <Box sx={{ width: 130, flexShrink: 0, display: "flex", justifyContent: "flex-end" }}>
      <Skeleton variant="text" width={80} height={20} />
    </Box>
    <Box sx={{ width: 180, flexShrink: 0, display: "flex", justifyContent: "flex-end" }}>
      <Skeleton variant="text" width={120} height={16} />
    </Box>
    <Stack direction="row" alignItems="center" gap="4px" sx={{ width: 110, flexShrink: 0, justifyContent: "flex-end" }}>
      <Skeleton variant="rounded" width={56} height={24} />
      <Skeleton variant="circular" width={26} height={26} />
    </Stack>
  </Stack>
);

interface AumRowViewProps {
  value: number;
  error?: string;
  canEdit: boolean;
  onCommit: (value: number) => void;
  onEditStart: () => void;
}

// AUM is the first row of the panel. Unlike the percentage constraints it has no
// AI sources / system-default / reset semantics — it's a single editable currency
// figure. The empty source/action columns keep its value aligned with the rows below.
const AumRowView: React.FC<AumRowViewProps> = ({ value, error, canEdit, onCommit, onEditStart }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");

  useEffect(() => {
    if (!isEditing) return;
    setEditValue(String(value));
    const id = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => cancelAnimationFrame(id);
  }, [isEditing, value]);

  const startEdit = () => {
    if (!canEdit) return;
    onEditStart();
    setIsEditing(true);
  };

  const commitEdit = () => {
    setIsEditing(false);
    const parsed = parseFloat(editValue.trim().replace(/[^\d.]/g, ""));
    // Skip the save if the value is unchanged (user opened the editor but didn't edit).
    if (Number.isFinite(parsed) && parsed !== value) onCommit(parsed);
  };

  const cancelEdit = () => setIsEditing(false);

  return (
    <Stack
      sx={{
        borderBottom: `1px solid ${COLOR_STROKE_DIVIDER}`,
        "&:last-of-type": { borderBottom: "none" }
      }}
    >
      <Stack direction="row" alignItems="center" gap="12px" sx={{ minHeight: "50px", padding: "10px 16px" }}>
        <Typography
          sx={{
            flex: "1 1 0",
            minWidth: 0,
            fontSize: "14px",
            lineHeight: "20px",
            color: COLOR_PRIMARY,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          AUM
        </Typography>

        <Box sx={{ width: 130, flexShrink: 0, textAlign: "right" }}>
          {isEditing ? (
            <NumberInput
              inputRef={inputRef}
              name="aum-value"
              value={editValue}
              onChange={(e) => setEditValue((e.target as HTMLInputElement).value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitEdit();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancelEdit();
                }
              }}
              onBlur={commitEdit}
              size="small"
              numberInputProps={{ decimalScale: 2, allowNegative: false }}
              InputProps={{
                endAdornment: (
                  <Typography sx={{ color: COLOR_SECONDARY, fontSize: "13px", lineHeight: "20px", ml: "4px" }}>
                    USD
                  </Typography>
                ),
                sx: {
                  fontSize: "15px",
                  fontWeight: 600,
                  color: COLOR_PRIMARY,
                  "& input": { textAlign: "right", padding: "4px 6px" },
                  "& fieldset": { borderColor: COLOR_INFO }
                }
              }}
              sx={{ width: 130 }}
            />
          ) : (
            <Typography
              component={canEdit ? "button" : "span"}
              onClick={canEdit ? startEdit : undefined}
              sx={{
                fontSize: "15px",
                fontWeight: 600,
                lineHeight: "20px",
                color: "#26a6ae",
                whiteSpace: "nowrap",
                background: "none",
                border: "none",
                padding: "2px 6px",
                borderRadius: "4px",
                cursor: canEdit ? "pointer" : "default",
                "&:hover": canEdit ? { backgroundColor: COLOR_BG_SUBTLE } : undefined
              }}
            >
              {new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(Number(value))}
            </Typography>
          )}
        </Box>

        {/* Empty source + action columns to keep the value aligned with the constraint rows. */}
        <Box sx={{ width: 180, flexShrink: 0 }} />
        <Box sx={{ width: 110, flexShrink: 0 }} />
      </Stack>
      {error && (
        <Typography
          sx={{
            color: COLOR_ERROR,
            fontSize: "11px",
            lineHeight: "14px",
            padding: "0 16px 8px"
          }}
        >
          {error}
        </Typography>
      )}
    </Stack>
  );
};

interface ObjectiveRowViewProps {
  constraint: ObjectiveConstraint;
  row: ObjectiveRow;
  isEditing: boolean;
  error?: string;
  canEdit: boolean;
  canShowReset: boolean;
  aiOpen: boolean;
  onStartEdit: () => void;
  onCommitEdit: (raw: string) => void;
  onCancelEdit: () => void;
  onToggleAI: (anchor: HTMLElement) => void;
  onReset: () => void;
}

const ObjectiveRowView: React.FC<ObjectiveRowViewProps> = ({
  constraint,
  row,
  isEditing,
  error,
  canEdit,
  canShowReset,
  aiOpen,
  onStartEdit,
  onCommitEdit,
  onCancelEdit,
  onToggleAI,
  onReset
}) => {
  const aiButtonRef = useRef<HTMLButtonElement | null>(null);
  const editInputRef = useRef<HTMLInputElement | null>(null);
  const [editValue, setEditValue] = useState("");

  useEffect(() => {
    if (!isEditing) return;
    setEditValue(String(row.value));
    const id = requestAnimationFrame(() => {
      editInputRef.current?.focus();
      editInputRef.current?.select();
    });
    return () => cancelAnimationFrame(id);
  }, [isEditing, row.value]);

  return (
    <Stack
      sx={{
        borderBottom: `1px solid ${COLOR_STROKE_DIVIDER}`,
        "&:last-of-type": { borderBottom: "none" }
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap="12px"
        sx={{
          minHeight: "50px",
          padding: "10px 16px"
        }}
      >
        <Typography
          sx={{
            flex: "1 1 0",
            minWidth: 0,

            fontSize: "14px",
            lineHeight: "20px",
            color: COLOR_PRIMARY,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          {constraint.label}
        </Typography>

        <Box sx={{ width: 130, flexShrink: 0, textAlign: "right" }}>
          {isEditing ? (
            <NumberInput
              inputRef={editInputRef}
              name="objective-value"
              value={editValue}
              onChange={(e) => setEditValue((e.target as HTMLInputElement).value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onCommitEdit(editValue);
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  onCancelEdit();
                }
              }}
              onBlur={() => onCommitEdit(editValue)}
              size="small"
              numberInputProps={{ decimalScale: 2, allowNegative: false }}
              InputProps={{
                endAdornment: constraint.suffix ? (
                  <Typography sx={{ color: COLOR_SECONDARY, fontSize: "13px", lineHeight: "20px", ml: "4px" }}>
                    {constraint.suffix}
                  </Typography>
                ) : undefined,
                sx: {
                  fontSize: "15px",
                  fontWeight: 600,
                  color: COLOR_PRIMARY,
                  "& input": { textAlign: "right", padding: "4px 6px" },
                  "& fieldset": { borderColor: COLOR_INFO }
                }
              }}
              sx={{ width: 130 }}
            />
          ) : (
            <Typography
              component={canEdit ? "button" : "span"}
              onClick={canEdit ? onStartEdit : undefined}
              sx={{
                fontSize: "15px",
                fontWeight: 600,
                lineHeight: "20px",
                color: "#26a6ae",
                whiteSpace: "nowrap",
                background: "none",
                border: "none",
                padding: "2px 6px",
                borderRadius: "4px",
                cursor: canEdit ? "pointer" : "default",
                "&:hover": canEdit ? { backgroundColor: COLOR_BG_SUBTLE } : undefined
              }}
            >
              {constraint.suffix === "%"
                ? new Intl.NumberFormat("en-US", { style: "percent", minimumFractionDigits: 2 }).format(
                    Number(row.value) / 100
                  )
                : new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: constraint.suffix,
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }).format(Number(row.value))}
            </Typography>
          )}
        </Box>

        <Box sx={{ width: 180, flexShrink: 0 }}>
          <SourceLabel row={row} />
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          gap="4px"
          sx={{ width: 110, flexShrink: 0, justifyContent: "flex-end" }}
        >
          {canEdit && (
            <Box
              component="button"
              ref={aiButtonRef}
              onClick={() => aiButtonRef.current && onToggleAI(aiButtonRef.current)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                padding: "4px 10px",
                borderRadius: "20px",
                border: `1px solid ${aiOpen ? COLOR_AI : COLOR_STROKE_IDLE}`,
                backgroundColor: aiOpen ? "rgba(124,58,237,0.08)" : "transparent",
                cursor: "pointer",
                color: aiOpen ? COLOR_AI : COLOR_PRIMARY,

                fontSize: "12px",
                fontWeight: 500,
                "&:hover": { borderColor: COLOR_AI, color: COLOR_AI }
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 14 }} /> AI
            </Box>
          )}
          {canShowReset && (
            <Box
              component="button"
              onClick={onReset}
              title="Reset to system default"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 26,
                height: 26,
                borderRadius: "50%",
                border: "none",
                backgroundColor: "transparent",
                cursor: "pointer",
                color: COLOR_SECONDARY,
                "&:hover": { backgroundColor: "rgba(7,31,53,0.06)", color: COLOR_PRIMARY }
              }}
            >
              <RestartAltIcon sx={{ fontSize: 16 }} />
            </Box>
          )}
        </Stack>
      </Stack>
      {error && (
        <Typography
          sx={{
            color: COLOR_ERROR,
            fontSize: "11px",
            lineHeight: "14px",
            padding: "0 16px 8px"
          }}
        >
          {error}
        </Typography>
      )}
    </Stack>
  );
};

const SourceLabel: React.FC<{ row: ObjectiveRow }> = ({ row }) => {
  if (row.source === "system-default") {
    return (
      <Typography
        sx={{
          fontSize: "13px",
          lineHeight: "16px",
          color: COLOR_DISABLED,
          textAlign: "right"
        }}
      >
        System Default
      </Typography>
    );
  }
  if (row.source === "user-override") {
    return (
      <Typography
        sx={{
          fontSize: "13px",
          lineHeight: "16px",
          color: COLOR_SECONDARY,
          textAlign: "right"
        }}
      >
        User override
      </Typography>
    );
  }
  const label = `AI · ${row.sourceLabel ?? "Source"}`;
  if (row.sourceUrl) {
    return (
      <Link
        href={row.sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        sx={{
          fontSize: "13px",
          lineHeight: "16px",
          color: COLOR_AI,
          textAlign: "right",
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}
      >
        {label}
      </Link>
    );
  }
  return (
    <Typography
      sx={{
        fontSize: "13px",
        lineHeight: "16px",
        color: COLOR_AI,
        textAlign: "right",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }}
    >
      {label}
    </Typography>
  );
};

interface AIPopoverContentProps {
  constraintKey: string;
  loadAISources: (key: string) => Promise<AISource[]>;
  onApply: (key: string, source: AISource) => void;
  onClose: () => void;
}

const AIPopoverContent: React.FC<AIPopoverContentProps> = ({ constraintKey, loadAISources, onApply, onClose }) => {
  const constraint = useMemo(() => constraintByKey(constraintKey), [constraintKey]);
  const [sources, setSources] = useState<AISource[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setSources(null);
    loadAISources(constraintKey)
      .then((res) => {
        setSources(res);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [constraintKey, loadAISources]);

  return (
    <Stack sx={{ width: "100%" }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        gap="12px"
        sx={{ padding: "12px 14px", borderBottom: `1px solid ${COLOR_STROKE_DIVIDER}` }}
      >
        <Stack sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" gap="6px">
            <AutoAwesomeIcon sx={{ fontSize: 14, color: COLOR_AI }} />
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                lineHeight: "18px",
                color: COLOR_PRIMARY
              }}
            >
              AI websearch · {constraint?.label ?? ""}
            </Typography>
          </Stack>
          <Typography
            sx={{
              fontSize: "12px",
              lineHeight: "16px",
              color: COLOR_SECONDARY,
              mt: "2px"
            }}
          >
            Recommended objective values from known providers.
          </Typography>
        </Stack>
        <Box
          component="button"
          onClick={onClose}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 22,
            height: 22,
            border: "none",
            backgroundColor: "transparent",
            color: COLOR_PRIMARY,
            cursor: "pointer",
            borderRadius: "50%",
            "&:hover": { backgroundColor: "rgba(7,31,53,0.06)" }
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </Box>
      </Stack>

      <Stack sx={{ padding: "6px 0", maxHeight: 280, overflowY: "auto" }}>
        {isLoading ? (
          <Stack alignItems="center" sx={{ py: "20px" }}>
            <CircularProgress size={20} sx={{ color: COLOR_AI }} />
          </Stack>
        ) : Boolean(sources) && Boolean(!sources?.length) ? (
          <Typography
            sx={{
              color: COLOR_DISABLED,
              fontSize: "13px",
              lineHeight: "18px",
              padding: "12px 14px",
              textAlign: "center"
            }}
          >
            No sources available for this constraint.
          </Typography>
        ) : (
          sources?.map((source) => {
            const hasValue = source.value != null;
            return (
              <Stack
                key={`${source.provider}-${source.url}`}
                direction="row"
                alignItems="center"
                gap="12px"
                sx={{
                  padding: "10px 14px"
                }}
              >
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 500,
                      lineHeight: "18px",
                      color: COLOR_SECONDARY,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {source.label || source.provider}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 700,
                      lineHeight: "22px",
                      color: COLOR_PRIMARY
                    }}
                  >
                    {hasValue ? `${source.value}${constraint?.suffix ?? ""}` : "—"}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    flexShrink: 0,
                    "& .button": {
                      padding: "5px 16px",
                      minHeight: 0,
                      fontSize: "12px",
                      maxWidth: "none",
                      overflow: "visible !important",
                      textOverflow: "clip"
                    }
                  }}
                >
                  <Button
                    variant="primary"
                    title="Apply"
                    disabled={!hasValue}
                    onClick={() => onApply(constraintKey, source)}
                  />
                </Box>
              </Stack>
            );
          })
        )}
      </Stack>
    </Stack>
  );
};
